import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const isElectron = !!window.electronAPI;

export default function Settings({ onClose }) {
  const [config, setConfig]     = useState(null);
  const [saving, setSaving]     = useState(false);
  const [testing, setTesting]   = useState(false);
  const [restarting, setRestart] = useState(false);
  const [pwForm, setPwForm]     = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [tab, setTab]           = useState('database');

  useEffect(() => {
    if (isElectron) {
      window.electronAPI.getConfig().then(cfg => setConfig(cfg || {}));
    }
  }, []);

  const set = k => e => setConfig(p => ({ ...p, [k]: e.target.value }));
  const setPw = k => e => setPwForm(p => ({ ...p, [k]: e.target.value }));

  const testConnection = async () => {
    setTesting(true);
    try {
      const r = await api.get('/ping');
      if (r.data.ok) toast.success('Connected to server ✓');
    } catch {
      toast.error('Cannot reach server — check MySQL and restart');
    } finally {
      setTesting(false);
    }
  };

  const saveAndRestart = async () => {
    if (!isElectron) return;
    setSaving(true);
    try {
      const result = await window.electronAPI.saveConfig(config);
      if (!result.ok) throw new Error(result.error);
      toast.success('Config saved');

      setRestart(true);
      const r = await window.electronAPI.restartBackend();
      if (r.ok) toast.success('Server restarted successfully');
      else toast.error('Server restart failed — ' + (r.error || 'unknown error'));
    } catch (err) {
      toast.error('Failed: ' + err.message);
    } finally {
      setSaving(false);
      setRestart(false);
    }
  };

  const changePassword = async () => {
    if (!pwForm.currentPassword || !pwForm.newPassword)
      return toast.error('Fill in all password fields');
    if (pwForm.newPassword !== pwForm.confirm)
      return toast.error('New passwords do not match');
    if (pwForm.newPassword.length < 6)
      return toast.error('New password must be at least 6 characters');
    setSaving(true);
    try {
      await api.post('/auth/change-password', {
        currentPassword: pwForm.currentPassword,
        newPassword: pwForm.newPassword,
      });
      toast.success('Password changed successfully');
      setPwForm({ currentPassword: '', newPassword: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const TABS = [
    ...(isElectron ? [{ key: 'database', label: 'Database' }] : []),
    { key: 'password', label: 'Change Password' },
  ];

  if (TABS.length === 1 && tab === 'database') setTab('password');

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 2000, padding: 24,
    }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: 'var(--surface)', borderRadius: 16,
        boxShadow: '0 24px 60px rgba(0,0,0,.22)',
        width: '100%', maxWidth: 520,
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 24px', borderBottom: '1px solid var(--border)',
        }}>
          <h2 style={{ fontSize: 17, fontWeight: 700 }}>Settings</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex', gap: 2, padding: '10px 24px 0',
          borderBottom: '1px solid var(--border)', background: 'var(--surface2)',
        }}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              padding: '7px 16px', border: 'none', cursor: 'pointer',
              background: 'none', fontFamily: 'inherit', fontSize: 13,
              fontWeight: tab === t.key ? 600 : 400,
              color: tab === t.key ? 'var(--primary)' : 'var(--text2)',
              borderBottom: tab === t.key ? '2px solid var(--primary)' : '2px solid transparent',
              marginBottom: -1,
            }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* ── Database tab ── */}
          {tab === 'database' && isElectron && (
            <>
              <p style={{ fontSize: 12, color: 'var(--text3)', margin: '-4px 0 4px' }}>
                These settings are saved to your local config file and applied when the server restarts.
              </p>
              {config === null ? (
                <p style={{ color: 'var(--text3)' }}>Loading…</p>
              ) : (
                <>
                  <div className="grid-2">
                    <div className="form-group">
                      <label className="form-label">DB Host</label>
                      <input className="form-input" value={config.DB_HOST || ''} onChange={set('DB_HOST')} placeholder="localhost" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">DB Port</label>
                      <input className="form-input" value={config.DB_PORT || ''} onChange={set('DB_PORT')} placeholder="3306" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">DB User</label>
                      <input className="form-input" value={config.DB_USER || ''} onChange={set('DB_USER')} placeholder="root" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">DB Password</label>
                      <input className="form-input" type="password" value={config.DB_PASSWORD || ''} onChange={set('DB_PASSWORD')} placeholder="••••••••" />
                    </div>
                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                      <label className="form-label">Database Name</label>
                      <input className="form-input" value={config.DB_NAME || ''} onChange={set('DB_NAME')} placeholder="economic_center" />
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 4 }}>
                    <button className="btn btn-secondary" onClick={testConnection} disabled={testing}>
                      {testing ? 'Testing…' : 'Test Connection'}
                    </button>
                    <button className="btn btn-primary" onClick={saveAndRestart} disabled={saving || restarting}>
                      {restarting ? 'Restarting…' : saving ? 'Saving…' : 'Save & Restart Server'}
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          {/* ── Password tab ── */}
          {tab === 'password' && (
            <>
              <div className="form-group">
                <label className="form-label">Current Password</label>
                <input className="form-input" type="password" value={pwForm.currentPassword}
                  onChange={setPw('currentPassword')} placeholder="Current password" />
              </div>
              <div className="form-group">
                <label className="form-label">New Password</label>
                <input className="form-input" type="password" value={pwForm.newPassword}
                  onChange={setPw('newPassword')} placeholder="At least 6 characters" />
              </div>
              <div className="form-group">
                <label className="form-label">Confirm New Password</label>
                <input className="form-input" type="password" value={pwForm.confirm}
                  onChange={setPw('confirm')} placeholder="Repeat new password" />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 4 }}>
                <button className="btn btn-primary" onClick={changePassword} disabled={saving}>
                  {saving ? 'Saving…' : 'Change Password'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
